import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import { Pause, Play, X } from 'lucide-react'
import { EmptyState } from '../cc/EmptyState'
import { Panel } from '../cc/Panel'
import { EYEBROW } from '../cc/tokens'
import { MbmtLiveMap, type MbmtReplayOverlay } from './MbmtLiveMap'
import {
  fetchMbmtGeotags,
  fetchMbmtTripAlertReplay,
  fetchMbmtTripAlerts,
  mbmtTripAlertCount,
  positionAt,
  todayIst,
  tripAlertsMinDate,
  MBMT_TRIP_ALERT_KINDS,
  type MbmtGeotagEvent,
  type MbmtTripAlertKind,
  type MbmtTripAlertReplay,
  type MbmtTripAlertRow,
} from './useMbmtApi'

const REPLAY_SPEEDS = [1, 4, 8, 16, 60] as const

const ALERT_KIND_LABEL: Record<MbmtTripAlertKind, string> = {
  HARSH_BRAKE: 'Harsh brake',
  HARSH_ACCEL: 'Harsh accel',
  OVERSPEED: 'Overspeed',
  UNSCHEDULED_STOP: 'Unsched. stop',
  EXCESSIVE_IDLING: 'Excess idling',
}

/** Same palette MbmtLiveMap's REPLAY_ALERT_COLOR uses for the map markers -- kept in sync
 * by hand (that constant is not exported) so a kind's colour reads the same in the table,
 * the replay legend and the map pin. */
const ALERT_KIND_COLOR: Record<MbmtTripAlertKind, string> = {
  HARSH_BRAKE: '#F43F5E',
  HARSH_ACCEL: '#F59E0B',
  OVERSPEED: '#DC2626',
  UNSCHEDULED_STOP: '#8B5CF6',
  EXCESSIVE_IDLING: '#0EA5E9',
}

const PAGE_SIZE = 50

/** started_ts/ended_ts arrive as epoch ms (v_mbmt_trip_alerts's own matched trip window,
 * serving/mbmt.py's _ms() same as every other MBMT timestamp -- see the MbmtTripAlertRow
 * comment in useMbmtApi.ts) -- formatted to "HH:MM" IST here, same
 * Intl.DateTimeFormat/toLocaleTimeString convention every other clock in this console
 * uses (e.g. MbmtDutyPlayback.tsx, MbmtEvidence.tsx). */
function hhmm(ms: number | null | undefined): string {
  if (ms == null) return '—'
  return new Date(ms).toLocaleTimeString('en-IN', {
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kolkata',
  })
}

/** One started–ended pair, the trip slot's own matched window (actual TIMS times when
 * both are real and valid, the scheduled window otherwise -- see v_mbmt_trip_alerts).
 * `completed === false` alone means "not finished yet", which is true of BOTH a trip
 * currently under way and one that hasn't left the depot -- `started` (now() >= the
 * matched window's start, the server's own derivation) is what actually tells them
 * apart. Three real states: not started (started=false) shows the scheduled pair, same
 * as a finished trip -- rendering "IN PROGRESS" for a bus that hasn't even departed is
 * the exact bug this field exists to fix. In progress (started=true, completed=false)
 * is the only state that reads "IN PROGRESS". Completed (completed=true) shows the real
 * pair. */
function legTiming(row: MbmtTripAlertRow): string {
  const start = hhmm(row.started_ts)
  return row.started && row.completed === false
    ? `${start} – IN PROGRESS`
    : `${start} – ${hhmm(row.ended_ts)}`
}

/**
 * MBMT's merged "Alerts" surface (Command Centre rework Phase 3 folded the standalone
 * live-actionables tab into this one) -- one row per scheduled site_duty_trip slot for the
 * picked day (GET /api/mbmt/trip-alerts), a count column per alert kind, and a Detail
 * window (every row, not just ones with alerts) showing that trip's GPS path, alert
 * markers, driver and schedule. Sits beside "Live ops" in BusOpsShell's own view switcher,
 * not a separate page.
 *
 * "Only with alerts" defaults ON: alert counts used to read as "mostly zero" before the
 * edge-dwell exclusion was scaled to the (often short, 10-20 minute) scheduled slot
 * instead of a flat 300s that could cancel the whole window -- hiding zero-alert trips by
 * default is what makes the page useful; the toggle lets someone see the full unfiltered
 * log. Paginated at a fixed 50 rows/page against the API's own `total` (the true matching
 * count, ignoring the page window) so a busy vehicle's many short trips never silently
 * fall off the end of a capped, unpaginated fetch.
 */
export function MbmtTripAlerts(): JSX.Element {
  const [rows, setRows] = useState<MbmtTripAlertRow[]>([])
  const [total, setTotal] = useState(0)
  const [offset, setOffset] = useState(0)
  const [date, setDate] = useState(todayIst)
  // Flips once the operator ever touches the date picker -- an always-open ops wall never
  // reloads, so without this the default would stay pinned to the day it mounted on
  // forever. Once someone has deliberately picked a date, auto-advancing out from under
  // them would be the more surprising behavior, so the rollover below stops entirely.
  const datePickedByUser = useRef(false)
  const [hasAlerts, setHasAlerts] = useState(true)
  const [loaded, setLoaded] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [detailFor, setDetailFor] = useState<MbmtTripAlertRow | null>(null)

  // Every load() call -- the initial mount, the 15s poll, and an offset/filter change --
  // goes through this one function, so one request-generation guard covers all three. A
  // poll in flight when Prev/Next or the filter fires starts a NEWER request without
  // waiting for the older one; without this guard, whichever resolves LAST wins, which is
  // not necessarily the newer one, and the page can flash back to stale rows after the
  // user has already moved on. Same "ignore it if something newer has started" shape the
  // replay modal below already uses (its own `live` flag), just generalized to a counter
  // since load() has three overlapping triggers, not one.
  const loadId = useRef(0)

  const load = useCallback(async () => {
    const id = ++loadId.current
    try {
      const page = await fetchMbmtTripAlerts({
        dateFrom: date, dateTo: date, hasAlerts, limit: PAGE_SIZE, offset,
      })
      if (loadId.current !== id) return
      setRows(page.rows)
      setTotal(page.total)
      setErr(null)
    } catch (e) {
      if (loadId.current !== id) return
      setErr(e instanceof Error ? e.message : 'trip alerts failed to load')
    } finally {
      if (loadId.current === id) setLoaded(true)
    }
  }, [date, hasAlerts, offset])

  useEffect(() => {
    // Guards the TIMER's own calls against piling up behind a still-in-flight poll --
    // same bug class as BusOpsShell.tsx's own refreshInFlightRef. Covers this effect's
    // own initial load() too, not just the timer's repeats: without that, a slow
    // initial load left the very first 15s tick free to fire a second, overlapping
    // call, since nothing had marked the initial one as in flight. Prev/Next and a
    // filter change still bypass this guard entirely -- they tear down this effect and
    // start a FRESH one (new `load` identity via its [date, hasAlerts, offset] deps),
    // so they get their own fresh, unset tickInFlight and are free to start immediately,
    // exactly as this file's own loadId "latest wins" guard above already expects.
    let tickInFlight = true
    void load().finally(() => { tickInFlight = false })
    const id = setInterval(() => {
      if (tickInFlight) return
      tickInFlight = true
      void load().finally(() => { tickInFlight = false })
    }, 15_000)
    return () => clearInterval(id)
  }, [load])

  // Carries the default date across an IST midnight rollover on an always-open wall --
  // without this, the picker (and every fetch) would stay pinned to whatever day the page
  // happened to mount on. No-ops once the operator has picked a date themselves.
  useEffect(() => {
    const id = setInterval(() => {
      if (datePickedByUser.current) return
      const today = todayIst()
      if (date === today) return
      // A wall left paginated into an earlier day's alerts at the moment IST rolls over
      // must not request the new day starting at the old page's offset.
      setOffset(0)
      setDate(today)
    }, 60_000)
    return () => clearInterval(id)
  }, [date])

  // The API already orders by started_ts DESC -- this just keeps that order stable if a
  // re-fetch ever lands rows in a different sequence for the same instant.
  // started_ts is null on a malformed bronze clock (rare, regex-guarded in the view) --
  // treat it as 0 rather than NaN, so a sort comparison never breaks down instead of
  // just sinking that one row to the bottom.
  const sorted = [...rows].sort((a, b) => (b.started_ts ?? 0) - (a.started_ts ?? 0))

  const pageStart = total === 0 ? 0 : offset + 1
  const pageEnd = Math.min(offset + rows.length, total)
  const hasPrev = offset > 0
  const hasNext = offset + PAGE_SIZE < total

  return (
    <div className="min-h-0 flex-1">
      <Panel className="h-full min-h-0" pad={false}>
        <div className="flex h-full min-h-0 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-[rgba(15,23,42,0.06)] px-4 py-2.5">
            <span className="text-[12px] font-semibold text-[#0F172A]">Alerts</span>
            <span className="text-[10.5px] text-[#94A3B8]">
              harsh brake · harsh accel · overspeed · unscheduled stop · excessive idling
            </span>
            <label className="flex items-center gap-1.5 text-[11px] font-medium text-[#64748B]">
              <input
                type="date"
                aria-label="Duty day"
                value={date}
                min={tripAlertsMinDate()}
                max={todayIst()}
                onChange={(e) => {
                  // The native picker fires an empty string mid-edit (all fields cleared)
                  // before the operator finishes typing a new one -- forwarding that would
                  // send dateFrom/dateTo='', which the API silently reads as "no filter"
                  // and falls back to its own 7-day trailing default while this picker
                  // keeps showing blank.
                  if (!e.target.value) return
                  // Feature 12b: clamp to the trailing-month window even if the browser
                  // ignores min/max (some mobile pickers still allow typing outside).
                  const min = tripAlertsMinDate()
                  const max = todayIst()
                  const next = e.target.value < min ? min : e.target.value > max ? max : e.target.value
                  datePickedByUser.current = true
                  setDate(next)
                  setOffset(0)
                }}
                className="rounded-[8px] border border-[rgba(15,23,42,0.10)] bg-white px-2 py-1 font-mono text-[11px] text-[#0F172A]"
              />
            </label>
            <button
              type="button"
              onClick={() => { setHasAlerts((v) => !v); setOffset(0) }}
              className={clsx(
                'ml-auto flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors',
                hasAlerts
                  ? 'bg-[rgba(3,105,161,0.1)] text-[#0369A1]'
                  : 'bg-[#F1F5F9] text-[#64748B]',
              )}
            >
              <span
                className={clsx(
                  'h-3 w-3 shrink-0 rounded-[3px] border',
                  hasAlerts ? 'border-[#0369A1] bg-[#0369A1]' : 'border-[#94A3B8] bg-white',
                )}
              />
              Only with alerts
            </button>
          </div>

          {err && (
            <div className="mx-4 mt-3 shrink-0 rounded-[10px] border border-[rgba(244,63,94,0.26)] bg-[rgba(244,63,94,0.08)] px-3 py-2 text-[12px] font-medium text-[#9F1239]">
              {err}
            </div>
          )}

          <div className="min-h-0 flex-1 overflow-auto">
            {!loaded ? (
              <div className="space-y-2 p-4">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className="h-10 animate-pulse rounded-[10px] bg-[#F1F5F9]" />
                ))}
              </div>
            ) : sorted.length === 0 ? (
              <EmptyState
                title="No trips with alerts"
                body={err ? undefined : 'Nothing recorded against a trip yet today.'}
              />
            ) : (
              <table className="w-full text-left text-[12px]">
                <thead className="sticky top-0 z-10 bg-[#F8FAFC] text-[10px] font-medium uppercase tracking-[0.1em] text-[#8794A7]">
                  <tr>
                    <th className="px-4 py-2 font-medium">Schedule No</th>
                    <th className="px-4 py-2 font-medium">Route</th>
                    <th className="px-4 py-2 font-medium">Trip No</th>
                    <th className="px-4 py-2 font-medium">Driver Name</th>
                    <th className="px-4 py-2 font-medium">Timing</th>
                    {MBMT_TRIP_ALERT_KINDS.map((k) => (
                      <th key={k} className="px-2 py-2 text-right font-medium" title={ALERT_KIND_LABEL[k]}>
                        <span className="inline-flex items-center gap-1 whitespace-nowrap">
                          <span
                            className="h-1.5 w-1.5 shrink-0 rounded-full"
                            style={{ backgroundColor: ALERT_KIND_COLOR[k] }}
                          />
                          {ALERT_KIND_LABEL[k]}
                        </span>
                      </th>
                    ))}
                    <th className="px-4 py-2 text-right font-medium">Detail</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[rgba(15,23,42,0.05)]">
                  {sorted.map((row) => {
                    const n = row.total_alerts
                    return (
                      <tr
                        key={row.trip_id}
                        onClick={() => setDetailFor(row)}
                        className={clsx(
                          'cursor-pointer text-[#334155] hover:bg-[#F8FAFC]',
                          n > 0 && 'bg-[rgba(245,158,11,0.03)]',
                        )}
                      >
                        <td className="whitespace-nowrap px-4 py-2 font-mono font-semibold text-[#0F172A]">
                          {row.schedule_code || '—'}
                        </td>
                        <td className="whitespace-nowrap px-4 py-2">{row.route || '—'}</td>
                        <td className="px-4 py-2">
                          <span className="font-medium text-[#0F172A]">
                            {row.origin_stage || '—'} - {row.destination_stage || '—'}
                          </span>
                          <span className="block font-mono text-[10px] text-[#94A3B8]">{row.vehicle_id}</span>
                        </td>
                        <td className="px-4 py-2">{row.driver_name || '—'}</td>
                        <td className="whitespace-nowrap px-4 py-2">
                          <span className="block tabular-nums">{legTiming(row)}</span>
                        </td>
                        {MBMT_TRIP_ALERT_KINDS.map((k) => {
                          const c = mbmtTripAlertCount(row, k)
                          return (
                            <td key={k} className="px-2 py-2 text-right tabular-nums">
                              {c > 0 ? (
                                <span className="font-semibold" style={{ color: ALERT_KIND_COLOR[k] }}>{c}</span>
                              ) : (
                                <span className="text-[#CBD5E1]">0</span>
                              )}
                            </td>
                          )
                        })}
                        <td className="px-4 py-2 text-right">
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setDetailFor(row) }}
                            title="Map, time, driver, schedule, and this trip's GPS path"
                            className="inline-flex items-center gap-1 rounded-full bg-[#0F172A] px-2.5 py-1 text-[11px] font-semibold text-white"
                          >
                            <Play size={10} />
                            Replay
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-3 border-t border-[rgba(15,23,42,0.06)] px-4 py-2">
            <span className="text-[11px] text-[#64748B]">
              {total === 0 ? 'No matching trips' : `Showing ${pageStart}–${pageEnd} of ${total}`}
            </span>
            <div className="ml-auto flex items-center gap-1.5">
              <button
                type="button"
                disabled={!hasPrev}
                onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
                className="rounded-full border border-[rgba(15,23,42,0.12)] px-2.5 py-1 text-[11px] font-semibold text-[#334155] disabled:opacity-30"
              >
                Prev
              </button>
              <button
                type="button"
                disabled={!hasNext}
                onClick={() => setOffset((o) => o + PAGE_SIZE)}
                className="rounded-full border border-[rgba(15,23,42,0.12)] px-2.5 py-1 text-[11px] font-semibold text-[#334155] disabled:opacity-30"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </Panel>

      {detailFor && (
        <MbmtTripDetailWindow row={detailFor} onClose={() => setDetailFor(null)} />
      )}
    </div>
  )
}

/** The merged alerts surface's per-row detail window (Command Centre rework Phase 3,
 * Feature 12 playback): map + trip GPS path + alert pins, with play/pause and a bottom
 * timeline that marks where each alert was generated (same contract as duty playback). */
function MbmtTripDetailWindow({ row, onClose }: {
  row: MbmtTripAlertRow
  onClose: () => void
}): JSX.Element {
  const [clip, setClip] = useState<MbmtTripAlertReplay | null>(null)
  const [geotags, setGeotags] = useState<MbmtGeotagEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)
  const [at, setAt] = useState(0)
  const [playing, setPlaying] = useState(false)
  const [speed, setSpeed] = useState(8)

  useEffect(() => {
    let live = true
    setLoading(true)
    setPlaying(false)
    setGeotags([])
    fetchMbmtTripAlertReplay(row.trip_id)
      .then(async (c) => {
        if (!live) return
        setClip(c)
        setErr(null)
        const start = c.started_ts ?? c.points[0]?.ts ?? 0
        setAt(start)
        // AC-12.4: trip-detail map must show geotags (same family as duty playback), not
        // an empty hardcoded array. Scope to the trip window on the duty day.
        const day = c.duty_day || row.duty_day || todayIst()
        try {
          const events = await fetchMbmtGeotags(c.vehicle_id || row.vehicle_id, day)
          if (!live) return
          const lo = c.started_ts ?? Number.NEGATIVE_INFINITY
          const hi = c.ended_ts ?? Number.POSITIVE_INFINITY
          setGeotags(events.filter((g) => g.ts >= lo && g.ts <= hi
            && Number.isFinite(g.lat) && Number.isFinite(g.lon)))
        } catch {
          if (live) setGeotags([])
        }
      })
      .catch((e) => { if (live) setErr(e instanceof Error ? e.message : 'replay failed to load') })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [row.trip_id, row.duty_day, row.vehicle_id])

  useEffect(() => {
    const esc = (e: KeyboardEvent): void => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', esc)
    return () => window.removeEventListener('keydown', esc)
  }, [onClose])

  const start = clip?.started_ts ?? clip?.points[0]?.ts ?? 0
  const lastPt = clip && clip.points.length > 0 ? clip.points[clip.points.length - 1] : undefined
  const end = clip?.ended_ts ?? lastPt?.ts ?? start
  const span = Math.max(1, end - start)
  const xOf = (ts: number) => Math.max(0, Math.min(100, ((ts - start) / span) * 100))

  useEffect(() => {
    if (!playing || !clip) return
    const id = window.setInterval(() => {
      setAt((prev) => {
        const next = prev + speed * 200
        if (next >= end) { setPlaying(false); return end }
        return next
      })
    }, 200)
    return () => window.clearInterval(id)
  }, [playing, speed, end, clip])

  const pos = useMemo(
    () => (clip ? positionAt(clip.points, at) : null),
    [clip, at],
  )

  const overlay: MbmtReplayOverlay | null = clip ? {
    line: clip.points
      .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon))
      .map((p) => [p.lon as number, p.lat as number] as [number, number]),
    playhead: pos && Number.isFinite(pos.lat) && Number.isFinite(pos.lon)
      ? [pos.lon as number, pos.lat as number] as [number, number]
      : null,
    geotags: geotags.map((g) => ({
      lon: g.lon as number,
      lat: g.lat as number,
      name: g.name || g.kind,
    })),
    alerts: clip.alerts
      .filter((a) => Number.isFinite(a.lat) && Number.isFinite(a.lon))
      .map((a) => ({ lon: a.lon as number, lat: a.lat as number, kind: a.kind, message: a.message })),
  } : null

  const alerts = clip?.alerts ?? []

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-slate-900/25 p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`${row.trip_id} replay`}
        className="flex h-[min(88vh,760px)] w-full max-w-4xl flex-col overflow-hidden rounded-[18px] border border-slate-200 bg-white shadow-2xl"
      >
        <header className="flex shrink-0 flex-wrap items-start gap-3 border-b border-slate-200/80 px-5 py-3.5">
          <div className="min-w-0 flex-1">
            <p className={EYEBROW}>Trip detail</p>
            <h2 className="mt-0.5 truncate text-[14px] font-semibold text-[#0F172A]">
              {row.vehicle_id} · {row.origin_stage || '—'} - {row.destination_stage || '—'}
            </h2>
            <p className="mt-0.5 text-[11px] font-medium text-[#0F172A]">
              {row.driver_name || 'No TIMS duty on this plate today'} · {legTiming(row)}
            </p>
            <p className="mt-0.5 text-[11px] text-[#64748B]">
              {row.schedule_code || 'no schedule'} · {row.route || 'no route on record'}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {MBMT_TRIP_ALERT_KINDS.map((k) => {
              const c = mbmtTripAlertCount(row, k)
              if (c === 0) return null
              return (
                <span
                  key={k}
                  className="flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold text-white"
                  style={{ backgroundColor: ALERT_KIND_COLOR[k] }}
                >
                  {ALERT_KIND_LABEL[k]} · {c}
                </span>
              )
            })}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-white text-slate-500 shadow-sm ring-1 ring-inset ring-slate-300 hover:text-slate-700"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="relative min-h-0 flex-1">
          {loading && (
            <div className="absolute inset-0 z-10 grid place-items-center bg-white/70 text-[12px] font-medium text-[#64748B]">
              Loading replay…
            </div>
          )}
          {err && (
            <div className="absolute left-3 top-3 z-10 rounded-[10px] border border-[rgba(244,63,94,0.26)] bg-[rgba(244,63,94,0.10)] px-3 py-2 text-[12px] font-medium text-[#9F1239]">
              {err}
            </div>
          )}
          {!loading && !err && overlay && overlay.line.length < 2 && (
            <div className="absolute inset-0 z-10 grid place-items-center bg-white/90 text-[12px] text-[#94A3B8]">
              No GPS trail recorded for this trip.
            </div>
          )}
          <MbmtLiveMap
            vehicles={[]}
            fleetVehicles={[]}
            routes={[]}
            stages={[]}
            down={new Set()}
            selected={null}
            onSelect={() => {}}
            replay={overlay}
            showFleetLegend={false}
          />
        </div>

        {/* Feature 12: playback controls + alert marks on the bottom timeline */}
        {clip && clip.points.length >= 2 && (
          <section className="shrink-0 border-t border-slate-200/80 p-3">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setPlaying((p) => !p)}
                className="flex h-8 items-center gap-1.5 rounded-full bg-[#0F172A] px-3 text-[12px] font-semibold text-white active:scale-[0.97]"
              >
                {playing ? <Pause size={12} /> : <Play size={12} />}
                {playing ? 'Pause' : 'Play'}
              </button>
              <span className="flex gap-0.5">
                {REPLAY_SPEEDS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setSpeed(s)}
                    className={clsx(
                      'h-8 rounded-full px-2 font-mono text-[11px]',
                      speed === s ? 'bg-[#0F172A] text-white' : 'text-[#64748B] hover:bg-[#EEF2F7]',
                    )}
                  >
                    {s}×
                  </button>
                ))}
              </span>
            </div>

            <p className={clsx(EYEBROW, 'mb-1')}>Alerts · {alerts.length}</p>
            <div className="relative mb-2 h-4 overflow-hidden rounded-full bg-[#EEF2F7]">
              {alerts.map((a, i) => (
                <button
                  key={`${a.alert_id ?? a.ts}-${i}`}
                  type="button"
                  title={`${hhmm(a.ts)} · ${a.kind}${a.message ? ` · ${a.message}` : ''}`}
                  onClick={() => { setAt(a.ts); setPlaying(false) }}
                  className={clsx(
                    'absolute top-0.5 h-3 w-3 -translate-x-1/2 rounded-full border-2 border-white',
                    a.severity === 'CRITICAL' ? 'bg-[#F43F5E]' : 'bg-[#F59E0B]',
                  )}
                  style={{ left: `${xOf(a.ts)}%` }}
                />
              ))}
              <span className="absolute top-1/2 h-px w-full -translate-y-1/2 bg-[rgba(245,158,11,0.18)]" aria-hidden />
              <span
                className="pointer-events-none absolute top-0 z-10 h-full w-0.5 bg-[#0F172A]"
                style={{ left: `${xOf(at)}%` }}
              />
            </div>

            <input
              type="range"
              min={start}
              max={end}
              value={Math.max(start, Math.min(end, at))}
              onChange={(e) => { setAt(Number(e.target.value)); setPlaying(false) }}
              className="w-full accent-[#0F172A]"
              aria-label="Trip playhead"
            />
            <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[12px] text-[#475569]">
              <span className="font-mono tabular-nums text-[#0F172A]">{hhmm(at)}</span>
              <span>{alerts.length} alerts · {clip.points.length} GPS</span>
            </div>
          </section>
        )}
      </div>
    </div>
  )
}
